/* eslint-disable */
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getToken, onMessage, isSupported } from "firebase/messaging";
import { db, messaging } from "../firebase";

const WEB_VAPID_KEY =
  "BNi2RrX19RvEMhXbfjG0Y3e4piPN_aCDiWShcINcBwwCjZOYko3QHM9IQqtZhngwXYiDSEO3MehXV4ELmwdCUew";

let nativeForegroundListenersAttached = false;

function isNative() {
  return Capacitor.isNativePlatform();
}

async function savePushToken(user, token, platform) {
  if (!user?.uid || !token) return null;

  console.log("[Push] Saving token", { uid: user.uid, platform, token });

  await setDoc(
    doc(db, "users", user.uid, "pushTokens", encodeURIComponent(token)),
    {
      token,
      platform,
      enabled: true,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );

  console.log("[Push] Token saved");
  return token;
}

export async function getPushPermissionState() {
  if (!isNative()) {
    if (typeof Notification === "undefined") return { receive: "denied" };

    return {
      receive:
        Notification.permission === "default"
          ? "prompt"
          : Notification.permission,
    };
  }

  try {
    const result = await PushNotifications.checkPermissions();
    console.log("[Push] Native permission state", result);
    return result;
  } catch (error) {
    console.warn("[Push] Permission check failed", error);
    return { receive: "denied" };
  }
}

export async function requestPushPermission() {
  if (!isNative()) {
    if (typeof Notification === "undefined") {
      return { granted: false, status: "denied" };
    }

    try {
      const result = await Notification.requestPermission();
      console.log("[Push] Web permission result", result);

      return {
        granted: result === "granted",
        status: result,
      };
    } catch (error) {
      console.warn("[Push] Web permission request failed", error);
      return { granted: false, status: "denied", error };
    }
  }

  try {
    const current = await PushNotifications.checkPermissions();
    console.log("[Push] Current native permission", current);

    if (current.receive === "granted") {
      return { granted: true, status: "granted" };
    }

    const requested = await PushNotifications.requestPermissions();
    console.log("[Push] Requested native permission", requested);

    return {
      granted: requested.receive === "granted",
      status: requested.receive,
    };
  } catch (error) {
    console.warn("[Push] Native permission request failed", error);
    return { granted: false, status: "denied", error };
  }
}

export async function registerForPush() {
  if (!isNative()) {
    console.log("[Push] Skipping native registration on web");
    return null;
  }

  try {
    const permission = await PushNotifications.checkPermissions();
    console.log("[Push] Register permission check", permission);

    if (permission.receive !== "granted") {
      console.warn("[Push] Cannot register. Permission not granted.");
      return null;
    }

    let settled = false;

    const tokenPromise = new Promise((resolve) => {
      const finish = (value) => {
        if (settled) return;
        settled = true;
        resolve(value || null);
      };

      const timeout = setTimeout(() => {
        console.warn("[Push] Registration timed out after 10 seconds");
        finish(null);
      }, 10000);

      PushNotifications.removeAllListeners()
        .then(() => {
          nativeForegroundListenersAttached = false;

          PushNotifications.addListener("registration", (token) => {
            console.log("[Push] Native registration success", token);
            clearTimeout(timeout);
            finish(token?.value || null);
          });

          PushNotifications.addListener("registrationError", (err) => {
            console.warn("[Push] Native registration error", err);
            clearTimeout(timeout);
            finish(null);
          });

          console.log("[Push] Calling PushNotifications.register()");
          return PushNotifications.register();
        })
        .catch((err) => {
          console.warn("[Push] Registration setup failed", err);
          clearTimeout(timeout);
          finish(null);
        });
    });

    return await tokenPromise;
  } catch (err) {
    console.warn("[Push] Registration failed", err);
    return null;
  }
}

export async function registerWebPush(user) {
  if (!user?.uid) return null;

  try {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return null;
    }

    const supported = await isSupported();

    if (!supported || !messaging || !("serviceWorker" in navigator)) {
      console.log("[Push] Web push not supported here");
      return null;
    }

    const permission = await requestPushPermission();
    if (!permission?.granted) return null;

    const sw = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: WEB_VAPID_KEY,
      serviceWorkerRegistration: sw,
    });

    if (!token) return null;

    return await savePushToken(user, token, "web");
  } catch (err) {
    console.warn("[Push] Web push unavailable", err);
    return null;
  }
}

export async function registerDeviceForPush(user) {
  if (!user?.uid) {
    console.warn("[Push] Cannot register. Missing user.");
    return null;
  }

  try {
    if (isNative()) {
      console.log("[Push] Registering native device");
      const token = await registerForPush();

      if (!token) {
        console.warn("[Push] No native push token returned");
        return null;
      }

      return await savePushToken(user, token, Capacitor.getPlatform());
    }

    console.log("[Push] Registering web push");
    return await registerWebPush(user);
  } catch (err) {
    console.warn("[Push] Registration unavailable", err);
    return null;
  }
}

export function listenForForegroundPush(onReceive) {
  if (isNative()) {
    if (nativeForegroundListenersAttached) {
      // already attached — no-op
      return undefined;
    }

    nativeForegroundListenersAttached = true;

    console.log("[Push] Attaching native foreground listeners");

    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.log("[Push] Foreground notification received", notification);

      onReceive?.({
        title: notification?.title || "New update",
        body: notification?.body || "",
        data: notification?.data || {},
      });
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      console.log("[Push] Notification action performed", action);

      onReceive?.({
        title: action?.notification?.title || "New update",
        body: action?.notification?.body || "",
        data: action?.notification?.data || {},
        clicked: true,
      });
    });

    return undefined;
  }

  if (!messaging) return undefined;

  console.log("[Push] Attaching web foreground listener");

  return onMessage(messaging, (payload) => {
    console.log("[Push] Web foreground notification received", payload);

    onReceive?.({
      title: payload?.notification?.title || "New update",
      body: payload?.notification?.body || "",
      data: payload?.data || {},
    });
  });
}